import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import React, { useRef, useEffect } from 'react';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';

interface TimelineHandlerProps {
  position: number | undefined;
  onMouseDown: (e: React.MouseEvent) => void;
  isDragging: boolean;
}

export const TimelineHandler: React.FC<TimelineHandlerProps> = ({
  position,
  onMouseDown,
  isDragging,
}) => {
  const selectedBorderColor = useColorModeValue('blue.500', 'blue.300');
  const handlerColor = useColorModeValue('white', 'gray.800');
  const scrollIntervalRef = useRef<number | null>(null);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        window.clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  // Handle auto-scrolling during drag
  useEffect(() => {
    if (isDragging) {
      const handleDragScroll = (e: MouseEvent) => {
        // Find the nearest scrollable parent element (could be the timeline container)
        const findScrollParent = (
          node: HTMLElement | null
        ): HTMLElement | null => {
          if (!node) return null;

          const overflowY = window.getComputedStyle(node).overflowY;
          const isScrollable =
            overflowY !== 'visible' && overflowY !== 'hidden';

          if (isScrollable && node.scrollHeight > node.clientHeight) {
            return node;
          }

          return findScrollParent(node.parentElement);
        };

        // Get the element under the cursor (where the handler is)
        const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY);
        const scrollParent = elementAtPoint
          ? findScrollParent(elementAtPoint as HTMLElement)
          : null;

        if (scrollParent) {
          const scrollParentRect = scrollParent.getBoundingClientRect();
          const scrollSensitivity = 60; // pixels from edge to start scrolling
          const scrollSpeed = 10; // pixels to scroll per interval

          // Clear any existing interval
          if (scrollIntervalRef.current) {
            window.clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }

          // Check if we're near the top or bottom edge
          if (e.clientY < scrollParentRect.top + scrollSensitivity) {
            // Near top edge, scroll up
            scrollIntervalRef.current = window.setInterval(() => {
              scrollParent.scrollBy(0, -scrollSpeed);
            }, 16); // ~60fps
          } else if (e.clientY > scrollParentRect.bottom - scrollSensitivity) {
            // Near bottom edge, scroll down
            scrollIntervalRef.current = window.setInterval(() => {
              scrollParent.scrollBy(0, scrollSpeed);
            }, 16); // ~60fps
          }
        }
      };

      const clearScrollInterval = () => {
        if (scrollIntervalRef.current) {
          window.clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      };

      // Add event listeners
      window.addEventListener('mousemove', handleDragScroll);
      window.addEventListener('mouseup', clearScrollInterval);

      return () => {
        window.removeEventListener('mousemove', handleDragScroll);
        window.removeEventListener('mouseup', clearScrollInterval);
        clearScrollInterval();
      };
    }
  }, [isDragging]);

  if (position === undefined) {
    return null;
  }

  return (
    <Flex
      position="absolute"
      left={0}
      right={0}
      top={position}
      transform="translateY(-50%)"
      height={4}
      zIndex={3}
      cursor="row-resize"
      alignItems="center"
      justifyContent="center"
      onMouseDown={onMouseDown}
    >
      {/* Handler knob */}
      <Box
        width={'32px'}
        height={3}
        backgroundColor={handlerColor}
        border="2px solid"
        borderColor={selectedBorderColor}
        borderRadius="full"
        zIndex={3}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <SparkelIcon icon={faEllipsis} size="lg" color={selectedBorderColor} />
      </Box>
    </Flex>
  );
};

export default TimelineHandler;
